import React, { useEffect, useState } from 'react';
import { Button, Form, Modal, Table } from 'react-bootstrap';
import axios from 'axios';
import '../../styles/SalesCommissionsPanel.css';

const money = value => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(value || 0));
const share = installment => ({ 1: 40, 2: 30, 3: 30 }[installment] || 0);

export default function SalesCommissionsPanel() {
  const [show, setShow] = useState(false);
  const [report, setReport] = useState({ scope: 'personal', viewer: null, items: [], summary: {} });
  const [config, setConfig] = useState({ rates: [], can_configure: false });
  const [rate, setRate] = useState({
    percentage: '', implementation_percentage: '35', effective_from: new Date().toISOString().slice(0, 10),
  });

  const load = async () => {
    const [reportResponse, configResponse] = await Promise.all([
      axios.get('/api/sales/commissions'), axios.get('/api/sales/commission-settings'),
    ]);
    setReport(reportResponse.data);
    setConfig(configResponse.data);
  };

  useEffect(() => { if (show) load(); }, [show]);

  const saveRate = async event => {
    event.preventDefault();
    const { data } = await axios.post('/api/sales/commission-settings', rate);
    setConfig(data);
  };

  const updateStatus = async (id, status) => {
    await axios.patch(`/api/sales/commissions/${id}/status`, { status });
    await load();
  };

  const labels = { sold: 'Vendido', blocked: 'Bloqueado', available: 'Disponível', approved: 'Aprovado', paid: 'Pago' };
  const statusLabels = {
    blocked: 'Aguardando marco', available: 'Disponível para aprovação', approved: 'Aprovada', paid: 'Paga', reversed: 'Estornada',
  };
  const formatDate = value => value ? new Date(value).toLocaleDateString('pt-BR') : '—';

  return <>
    <button className="sales-report-trigger" onClick={() => setShow(true)}>Vendas e comissões</button>
    <Modal show={show} onHide={() => setShow(false)} size="xl" centered className="sales-report-modal">
      <Modal.Header closeButton>
        <Modal.Title>{report.scope === 'personal' ? 'Minhas vendas e comissões' : 'Vendas e comissões da equipe'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {report.scope === 'personal' && <div className="sales-personal-intro">
          <strong>Olá, {report.viewer?.name}</strong>
          <span>Acompanhe cada venda e a liberação das suas comissões.</span>
        </div>}

        <div className="sales-report-summary">
          {Object.keys(labels).map(key => <div key={key}><small>{labels[key]}</small><strong>{money(report.summary[key])}</strong></div>)}
        </div>

        {config.can_configure && <Form onSubmit={saveRate} className="sales-rate-form">
          <div><strong>Percentuais por vigência</strong><small>Aplicados aos pagamentos confirmados a partir da data.</small></div>
          <Form.Group><Form.Label>Venda (%)</Form.Label><Form.Control required type="number" min="0" max="100" step=".01" placeholder="Ex.: 20" value={rate.percentage} onChange={event => setRate({ ...rate, percentage: event.target.value })} /></Form.Group>
          <Form.Group><Form.Label>Implantação (%)</Form.Label><Form.Control required type="number" min="0" max="100" step=".01" placeholder="Ex.: 35" value={rate.implementation_percentage} onChange={event => setRate({ ...rate, implementation_percentage: event.target.value })} /></Form.Group>
          <Form.Group><Form.Label>Vigência</Form.Label><Form.Control required type="date" value={rate.effective_from} onChange={event => setRate({ ...rate, effective_from: event.target.value })} /></Form.Group>
          <Button type="submit">Salvar percentuais</Button>
        </Form>}

        <p className="sales-commission-help"><strong>Como é calculado:</strong> venda e implantação são liberadas em 40%, 30% e 30%. Os marcos dependem do checklist obrigatório disponível no card do lead.</p>
        <div className="sales-rate-history">{config.rates.map(item => <span key={item.id}><b>Venda {item.percentage}% · Implantação {item.implementation_percentage ?? 35}%</b> desde {new Date(`${item.effective_from}T12:00`).toLocaleDateString('pt-BR')}</span>)}</div>

        <Table responsive hover className="sales-report-table">
          <thead><tr><th>Cliente</th><th>Responsável</th><th>Parcela</th><th>Base</th><th>Valor</th><th>Status e progressão</th><th></th></tr></thead>
          <tbody>{report.items.map(item => <tr key={item.id}>
            <td><strong>{item.customer_name}</strong><small>{item.plan_name}</small></td>
            <td>{item.beneficiary_name || 'Não atribuído'}</td>
            <td>{item.type === 'implementation' ? 'Implantação' : 'Venda'} {item.installment}/3 — {share(item.installment)}% da comissão</td>
            <td>{money(item.base_amount)}</td>
            <td><strong>{money(item.amount)}</strong><small>{item.rate}% × {share(item.installment)}% da comissão</small></td>
            <td><span className={`sales-status sales-status--${item.status}`}>{statusLabels[item.status] || item.status}</span><small>{item.status === 'blocked' ? `Liberação prevista: ${formatDate(item.eligibility_at)}` : item.paid_at ? `Pago em ${formatDate(item.paid_at)}` : item.approved_at ? `Aprovado em ${formatDate(item.approved_at)}` : item.available_at ? `Disponível desde ${formatDate(item.available_at)}` : ''}</small></td>
            <td>
              {item.status === 'blocked' && <span className="sales-checklist-pending">Concluir checklist no card do lead</span>}
              {config.can_configure && item.status === 'available' && <Button size="sm" onClick={() => updateStatus(item.id, 'approved')}>Aprovar</Button>}
              {config.can_configure && item.status === 'approved' && <Button size="sm" variant="success" onClick={() => updateStatus(item.id, 'paid')}>Pagar</Button>}
            </td>
          </tr>)}</tbody>
        </Table>
        {!report.items.length && <div className="sales-empty-state"><strong>Nenhuma venda vinculada a você ainda.</strong><span>Quando uma contratação paga for atribuída ao seu usuário, a progressão aparecerá aqui.</span></div>}
      </Modal.Body>
    </Modal>
  </>;
}